import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { emailLogsApi } from '../../services/dispatchApi';
import { LoadingState, EmptyState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';
const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

const PURPOSE_LABEL = {
  otp_verify: 'Registration OTP',
  otp_forgot: 'Password reset OTP',
  welcome: 'Welcome',
  password_changed: 'Password changed',
  contact_ack: 'Contact acknowledgement',
  order_confirmation: 'Order confirmation',
  dispatch: 'Dispatch / shipping',
  smtp_test: 'SMTP test',
  transactional: 'Transactional',
};

const PURPOSE_BADGE = {
  otp_verify: 'bg-indigo-50 text-indigo-800 border-indigo-100',
  otp_forgot: 'bg-violet-50 text-violet-800 border-violet-100',
  welcome: 'bg-emerald-50 text-emerald-800 border-emerald-100',
  password_changed: 'bg-amber-50 text-amber-800 border-amber-100',
  contact_ack: 'bg-sky-50 text-sky-800 border-sky-100',
  order_confirmation: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-100',
  dispatch: 'bg-rose-50 text-rose-800 border-rose-100',
  smtp_test: 'bg-gray-100 text-gray-700 border-gray-200',
  transactional: 'bg-slate-50 text-slate-800 border-slate-200',
};

const PERIOD_HINT = {
  today: 'Calendar day',
  week: 'Week to date',
  month: 'Month to date',
};

function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatCard({ label, value, hint, icon, iconBg }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className="text-2xl font-extrabold text-gray-900 leading-tight">{value}</div>
          {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

export default function EmailLogsSection() {
  const [period, setPeriod] = useState('today');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [purpose, setPurpose] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ last_page: 1, total: 0, current_page: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await emailLogsApi.list({
        period,
        q: q.trim() || undefined,
        status: status || undefined,
        purpose: purpose || undefined,
        page,
        per_page: 30,
      });
      setSummary(data.summary || null);
      const paginator = data.logs || {};
      setLogs(paginator.data || []);
      setMeta({
        last_page: paginator.last_page || 1,
        total: paginator.total || 0,
        current_page: paginator.current_page || 1,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not load email logs';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [period, q, status, purpose, page]);

  useEffect(() => {
    load();
  }, [load]);

  const periods = [
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This week' },
    { id: 'month', label: 'This month' },
  ];

  const filtersActive = Boolean(status || purpose || q.trim());
  const clearFilters = () => {
    setStatus('');
    setPurpose('');
    setQ('');
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-1" style={{ color: FLARE_RED }}>
            Email Center
          </p>
          <h1 className="admin-page-title">Send History</h1>
          <p className="admin-page-sub">
            Delivery log for every outbound email — sent, failed, or skipped when disabled in Notifications & Templates.
            OTP codes are never stored.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <Link
            to="/admin/email-center"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm"
          >
            <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-4 w-4" />
            Notifications & Templates
          </Link>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm"
          >
            <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Sent today"
          value={summary?.today ?? 0}
          hint="Calendar day"
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          iconBg="#059669"
        />
        <StatCard
          label="This week"
          value={summary?.week ?? 0}
          hint="Week to date"
          icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          iconBg={FLARE_RED}
        />
        <StatCard
          label="This month"
          value={summary?.month ?? 0}
          hint="Month to date"
          icon="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          iconBg="#4f46e5"
        />
        <StatCard
          label="Failed in view"
          value={summary?.failed ?? 0}
          hint="Selected period"
          icon="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          iconBg="#d97706"
        />
        <StatCard
          label="Skipped in view"
          value={summary?.skipped ?? 0}
          hint="Disabled in Email Center"
          icon="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          iconBg="#64748b"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Period</label>
            <div className="mt-1 inline-flex w-full rounded-lg bg-gray-100 p-1">
              {periods.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setPeriod(p.id); setPage(1); }}
                  className={`flex-1 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                    period === p.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped (disabled)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Type</label>
            <select
              value={purpose}
              onChange={(e) => { setPurpose(e.target.value); setPage(1); }}
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
            >
              <option value="">All types</option>
              {Object.entries(PURPOSE_LABEL).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Search</label>
            <input
              type="search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Email, name, subject…"
              className="w-full mt-1 text-sm border border-gray-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: '#fff1f2', color: '#9f1016', border: '1px solid rgba(196,21,28,0.15)' }}
          >
            <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-3.5 w-3.5" />
            {periods.find((p) => p.id === period)?.label} · {PERIOD_HINT[period]} · {meta.total} matching
          </div>
          {filtersActive ? (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-1 px-2.5 py-1 rounded-md bg-white border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">Send history</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Updating…' : `${meta.total} ${meta.total === 1 ? 'email' : 'emails'} in this view`}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingState label="Loading email logs" />
        ) : loadError && logs.length === 0 ? (
          <EmptyState title="Could not load email logs" hint={loadError}>
            <button
              type="button"
              onClick={load}
              className="px-6 py-2.5 rounded-lg text-white font-bold shadow"
              style={{ backgroundColor: FLARE_RED }}
            >
              Try again
            </button>
          </EmptyState>
        ) : logs.length === 0 ? (
          <EmptyState
            title="No emails in this period"
            hint={filtersActive
              ? 'Try clearing filters or choosing a different period.'
              : 'Sends from register, password reset, packing, and plugin tests will appear here.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Sent</th>
                  <th className="px-6 py-3 text-left font-semibold">Recipient</th>
                  <th className="px-6 py-3 text-left font-semibold">Type</th>
                  <th className="px-6 py-3 text-left font-semibold">Subject</th>
                  <th className="px-6 py-3 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((row, idx) => (
                  <tr key={row.id} className={`hover:bg-rose-50/30 ${idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{formatWhen(row.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900">{row.to_email}</div>
                      {row.to_name ? <div className="text-xs text-gray-500 mt-0.5">{row.to_name}</div> : null}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${PURPOSE_BADGE[row.purpose] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {PURPOSE_LABEL[row.purpose] || row.purpose}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700 max-w-xs truncate">{row.subject || '—'}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border ${
                          row.status === 'sent'
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-100'
                            : row.status === 'skipped'
                              ? 'bg-slate-100 text-slate-700 border-slate-200'
                              : 'bg-amber-50 text-amber-800 border-amber-200'
                        }`}
                      >
                        {row.status === 'sent' ? 'Sent' : row.status === 'skipped' ? 'Skipped' : 'Failed'}
                      </span>
                      {row.error ? (
                        <div className="mt-1 text-[11px] text-amber-700 max-w-[220px] truncate" title={row.error}>
                          {row.error}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-100 bg-gray-50">
            <div className="text-sm text-gray-600">
              Total {meta.total} emails · Page {meta.current_page} / {meta.last_page}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={page >= meta.last_page}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 bg-white border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
